import { useEffect, useState } from 'react';

import { toast } from 'sonner';

import { NotificationItemDTOCategory } from '@/api';
import { NotificationItem, NotificationToastItem } from '@/components/Notification/NotificationToastItem';

import { useDisplayedNotifications } from './useDisplayedNotifications';
import { useNotificationsFilters } from './useNotificationsFilters';

const useToastNotifications = () => {
  const { filteredNotifications, isLoading, isError } = useNotificationsFilters();
  const { isRead, markAsRead } = useDisplayedNotifications();
  // Toast countdowns run one at a time: only the notification at the head of the queue counts down,
  // the ones behind it wait paused.
  const [queue, setQueue] = useState<NotificationItem[]>([]);

  const dequeue = (id: string) => setQueue((current) => current.filter((queued) => queued.id !== id));

  useEffect(() => {
    if (isLoading || isError || filteredNotifications.length === 0) return;

    // The toaster only surfaces actionable notifications — general information ones stay in the
    // notification center only.
    const unread = filteredNotifications.filter(
      (notification) =>
        !isRead(notification.id) && notification.category !== NotificationItemDTOCategory.GENERAL_INFORMATIONS
    );

    if (unread.length === 0) return;

    unread.forEach((notification) => markAsRead(notification.id));

    // Queued in reverse so the last notification counts down first, and appended rather than
    // replacing so notifications from a later refetch cannot cut in front of a running countdown.
    setQueue((current) => [
      ...current,
      ...[...unread].reverse().filter((notification) => !current.some((queued) => queued.id === notification.id)),
    ]);
  }, [filteredNotifications, isLoading, isError]);

  useEffect(() => {
    const countingDownId = queue[0]?.id;

    // Sonner stacks the most recent toast at the front, so the toasts are created from the back of
    // the stack to the front — the head of the queue is created last and ends up in front. Toasts
    // that already exist are updated in place, which is what hands the countdown over when the
    // queue changes.
    [...queue].reverse().forEach((notification) => {
      toast.custom(
        (toastId) => (
          <NotificationToastItem
            notification={notification}
            toastId={toastId}
            paused={notification.id !== countingDownId}
            onFinished={() => dequeue(notification.id)}
          />
        ),
        {
          id: notification.id,
          duration: Infinity,
          unstyled: true,
        }
      );
    });
  }, [queue]);
};

export { useToastNotifications };
